import React from 'react';
import moment from 'moment';
import './customerCopyPrint.css';

const value = (item) => (item === undefined || item === null || item === '' ? '-' : item);
const amount = (item) => Number(item || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CustomerCopySlip = React.forwardRef((props, ref) => {
  const {
    bookingOrderNumber,
    bookingDate,
    customer = {},
    upperData = [],
    lowerData = [],
    selectedUpperItemDetails = {},
    selectedLowerItemDetails = {},
    upperQnty = 1,
    lowerQnty = 1,
    billingItems = [],
    settlement = {},
  } = props;

  const upperMeasurements = upperData.filter((item) => item.size !== '');
  const lowerMeasurements = lowerData.filter((item) => item.size !== '');
  const clothItems = billingItems.filter((item) =>
    String(item.SECTION_NAME || item.section_name || '').toLowerCase().includes('fabric')
  );
  const itemSubtotal = billingItems.reduce(
    (total, item) => total + Number(item.AMOUNT ?? Number(item.RATE || 0) * Number(item.QNTY || 0)),
    0
  );
  const netPayable = Number(billingItems[0]?.GTOTAL ?? itemSubtotal);
  const paidAmount = ['cashAmount', 'cardAmount', 'epaymentAmount', 'btcAmount', 'advanceAmount'].reduce(
    (total, key) => total + Number(settlement[key] || 0),
    0
  );
  const hasPayment = paidAmount > 0;

  return (
    <div
      ref={ref}
      id="print-customer-copy"
      style={{ width: '794px', background: '#fff', color: '#111827', fontFamily: 'Arial, sans-serif', padding: '28px', boxSizing: 'border-box' }}
    >
      <div style={{ borderBottom: '2px solid #111827', paddingBottom: '12px', textAlign: 'center' }}>
        <div style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '1px' }}>CUSTOMER COPY</div>
        <div style={{ fontSize: '13px', marginTop: '4px' }}>Booking, measurements and billing details</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '16px 0', fontSize: '13px' }}>
        <div><strong>Booking No.:</strong> {value(bookingOrderNumber)}</div>
        <div><strong>Date:</strong> {moment(bookingDate).format('DD-MM-YYYY')}</div>
      </div>

      <Section title="Customer Details">
        <Details rows={[
          ['Customer Name', customer.customer_name], ['Mobile', customer.mobile],
          ['Email', customer.email], ['Address', [customer.address, customer.city, customer.state, customer.pin_code].filter(Boolean).join(', ')],
          ['Company', customer.company_name], ['Company Phone', customer.company_phone],
        ]} />
      </Section>

      <Section title="Garment & Measurement Details">
        {upperMeasurements.length > 0 && (
          <MeasurementMatrix
            title="UPPER SIZE"
            garment={selectedUpperItemDetails.item_name}
            quantity={upperQnty}
            measurements={upperMeasurements}
          />
        )}
        {lowerMeasurements.length > 0 && (
          <MeasurementMatrix
            title="LOWER SIZE"
            garment={selectedLowerItemDetails.item_name}
            quantity={lowerQnty}
            measurements={lowerMeasurements}
          />
        )}
        {!upperMeasurements.length && !lowerMeasurements.length && (
          <div style={{ fontSize: '12px', textAlign: 'center' }}>
            No measurements added
          </div>
        )}
      </Section>

      {clothItems.length > 0 && (
        <Section title="Cloth Details">
          <Table
            headers={['Cloth / Item', 'Qty', 'Rate', 'Amount']}
            rows={clothItems.map((item) => [item.ITEM_NAME || item.item_name, item.QNTY, amount(item.RATE), amount(item.AMOUNT ?? Number(item.RATE || 0) * Number(item.QNTY || 0))])}
            emptyText="No cloth details added"
          />
        </Section>
      )}

      <Section title="Billing Details">
        {settlement.invoiceNumber && (
          <div style={{ fontSize: '13px', marginBottom: '8px' }}>
            <strong>Invoice No.:</strong> {settlement.invoiceNumber}
          </div>
        )}
        <Table
          headers={['Item', 'Qty', 'Rate', 'Amount']}
          rows={billingItems.map((item) => [item.ITEM_NAME || item.item_name, item.QNTY, amount(item.RATE), amount(item.AMOUNT ?? Number(item.RATE || 0) * Number(item.QNTY || 0))])}
          emptyText="No billing items added"
        />
        <div style={{ marginTop: '10px', marginLeft: 'auto', width: '300px', fontSize: '13px' }}>
          <Summary label="Item Subtotal" value={amount(itemSubtotal)} />
          <Summary label="Net Payable" value={amount(netPayable)} />
          {Number(settlement.cashAmount) > 0 && (
            <Summary label="Cash Paid" value={amount(settlement.cashAmount)} />
          )}
          {Number(settlement.cardAmount) > 0 && (
            <Summary label="Card Paid" value={amount(settlement.cardAmount)} />
          )}
          {Number(settlement.epaymentAmount) > 0 && (
            <Summary
              label={settlement.epaymentType ? `ePayment (${settlement.epaymentType})` : 'ePayment'}
              value={amount(settlement.epaymentAmount)}
            />
          )}
          {Number(settlement.btcAmount) > 0 && (
            <Summary label="BTC Paid" value={amount(settlement.btcAmount)} />
          )}
          {Number(settlement.advanceAmount) > 0 && (
            <Summary label="Advance Paid" value={amount(settlement.advanceAmount)} />
          )}
          {hasPayment && <Summary label="Total Paid" value={amount(paidAmount)} />}
          {Number(settlement.clothAmount) > 0 && (
            <Summary label="Cloth Balance" value={amount(settlement.clothAmount)} />
          )}
          {Number(settlement.DISCOUNT_AMOUNT) > 0 && (
            <Summary label="Discount" value={amount(settlement.DISCOUNT_AMOUNT)} />
          )}
        </div>
        {!hasPayment && netPayable > 0 && (
          <div style={{ marginTop: '12px', border: '1px solid #b91c1c', background: '#fef2f2', color: '#991b1b', padding: '8px 10px', fontSize: '13px', fontWeight: 700, textAlign: 'center' }}>
            Payment Due: ₹ {amount(netPayable)}
          </div>
        )}
      </Section>

      <div style={{ borderTop: '1px solid #9ca3af', marginTop: '20px', paddingTop: '10px', fontSize: '11px', textAlign: 'center' }}>
        This is a customer copy. Please keep it for your records.
      </div>
    </div>
  );
});

const Section = ({ title, children }) => (
  <section style={{ marginTop: '18px' }}>
    <div style={{ background: '#e5e7eb', border: '1px solid #6b7280', fontSize: '14px', fontWeight: 700, padding: '7px 9px' }}>{title}</div>
    <div style={{ border: '1px solid #9ca3af', borderTop: 0, padding: '10px' }}>{children}</div>
  </section>
);

const Details = ({ rows }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', fontSize: '13px' }}>
    {rows.map(([label, item]) => <div key={label}><strong>{label}:</strong> {value(item)}</div>)}
  </div>
);

const MeasurementMatrix = ({ title, garment, quantity, measurements }) => {
  const columnsPerTable = 10;
  const groups = [];

  for (let index = 0; index < measurements.length; index += columnsPerTable) {
    groups.push(measurements.slice(index, index + columnsPerTable));
  }

  return groups.map((group, groupIndex) => (
    <table
      key={`${title}-${groupIndex}`}
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        fontSize: '10px',
        marginTop: groupIndex === 0 ? 0 : '7px',
        marginBottom: title === 'UPPER SIZE' && groupIndex === groups.length - 1 ? '9px' : 0,
      }}
    >
      <thead>
        <tr>
          <th
            colSpan={group.length + 2}
            style={{ border: '1px solid #6b7280', background: '#d1d5db', padding: '4px', fontSize: '11px' }}
          >
            {title}
          </th>
        </tr>
        <tr>
          <th style={{ ...measurementCell, width: '105px', textAlign: 'left' }}>Garment</th>
          <th style={{ ...measurementCell, width: '36px' }}>Qty</th>
          {group.map((item, index) => (
            <th
              key={`${item.PARAMETER || item.parameter}-${index}`}
              style={{ ...measurementCell, overflowWrap: 'anywhere' }}
            >
              {item.PARAMETER || item.parameter}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={{ ...measurementCell, textAlign: 'left', fontWeight: 700 }}>
            {value(garment)}
          </td>
          <td style={measurementCell}>{value(quantity)}</td>
          {group.map((item, index) => (
            <td key={`${item.PARAMETER || item.parameter}-size-${index}`} style={measurementCell}>
              {value(item.size)}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  ));
};

const measurementCell = {
  border: '1px solid #9ca3af',
  padding: '4px 3px',
  textAlign: 'center',
  lineHeight: 1.15,
};

const Table = ({ headers, rows, emptyText }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
    <thead><tr>{headers.map((header) => <th key={header} style={{ background: '#f3f4f6', border: '1px solid #9ca3af', padding: '6px', textAlign: header === 'Parameter' || header.includes('Item') || header.includes('Cloth') ? 'left' : 'right' }}>{header}</th>)}</tr></thead>
    <tbody>{rows.length ? rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex} style={{ border: '1px solid #d1d5db', padding: '6px', textAlign: cellIndex === 0 ? 'left' : 'right' }}>{value(cell)}</td>)}</tr>) : <tr><td colSpan={headers.length} style={{ border: '1px solid #d1d5db', padding: '8px', textAlign: 'center' }}>{emptyText}</td></tr>}</tbody>
  </table>
);

const Summary = ({ label, value: summaryValue }) => <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #9ca3af', padding: '4px 0' }}><strong>{label}</strong><span>₹ {summaryValue}</span></div>;

export default CustomerCopySlip;
